/**
 * Search Suggestion List Component
 *
 * Displays a list of suggested search prompts with chat_round_call icon.
 * When availableHeight (px) is passed, only shows as many items as fit (4, 2, or 0); otherwise uses count.
 */

'use client';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { spacing } from '@/design-system/utils';
import PromptButton from '@/components/common/PromptButton';
import { getRandomTags } from '@/store/headerStore';

/** Height (px) per row of suggestions (2 items per row on mobile) */
const ROW_HEIGHT = 40;

interface SearchSuggestionListProps {
  count?: number;
  /** When set (e.g. on mobile), only show 4, 2, or 0 suggestions that fit in this height; otherwise skip suggestion list */
  availableHeight?: number;
  onPromptClick?: (prompt: string) => void;
  className?: string;
}

function getDisplayCount(count: number, availableHeight?: number): number {
  if (availableHeight == null || availableHeight <= 0) return count;
  if (availableHeight >= ROW_HEIGHT * 4) return Math.min(4, count);
  if (availableHeight >= ROW_HEIGHT * 3) return Math.min(3, count);
  if (availableHeight >= ROW_HEIGHT * 2) return Math.min(2, count);
  if (availableHeight >= ROW_HEIGHT) return Math.min(1, count);

  return 0;
}

export function SearchSuggestionList({
  count = 4,
  availableHeight,
  onPromptClick,
  className = '',
}: SearchSuggestionListProps) {
  const computedCount = useMemo(
    () => getDisplayCount(count, availableHeight),
    [count, availableHeight]
  );

  // Freeze count when using availableHeight (mobile) so scroll cannot increase suggestions
  const frozenCountRef = useRef<number | null>(null);
  if (availableHeight == null || availableHeight <= 0) {
    frozenCountRef.current = null;
  } else if (frozenCountRef.current === null) {
    frozenCountRef.current = computedCount;
  } else {
    frozenCountRef.current = Math.min(frozenCountRef.current, computedCount);
  }
  const displayCount =
    availableHeight != null && availableHeight > 0
      ? (frozenCountRef.current ?? computedCount)
      : computedCount;

  const [prompts, setPrompts] = useState<string[]>([]);

  // Initialize random prompts on client side from headerStore (always fetch up to 4, then slice)
  useEffect(() => {
    if (typeof window !== 'undefined' && displayCount > 0) {
      setPrompts(getRandomTags(4).slice(0, displayCount));
    }
  }, [displayCount]);

  const handlePromptClick = (prompt: string) => {
    if (onPromptClick) {
      onPromptClick(prompt);
    } else {
      console.log('Clicked:', prompt);
    }
  };

  if (displayCount === 0) return null;

  const toShow = prompts.slice(0, displayCount);

  return (
    <div
      className={`landing-suggestion-list ${className} grid grid-cols-1 md:grid-cols-2 w-full`}
      style={{
        gap: spacing[3], // 12px gap between items
        paddingBottom: 4,
      }}
    >
      {toShow.map((prompt, index) => (
        <div key={index} style={{ width: '100%', minWidth: 0 }}>
          <PromptButton
            prompt={prompt}
            onClick={() => handlePromptClick(prompt)}
            className='w-full'
          />
        </div>
      ))}
    </div>
  );
}

export default SearchSuggestionList;
